import { countBy, pluck } from 'ramda';
import React, { FC, useCallback, useEffect } from 'react';
import { usePrevious } from 'react-use';
import { FormattedMessage } from 'react-intl';
import { FullHeightLoader } from '../../../components/common/FullHeightLoader';
import { SectionsLayout } from '../../../components/layouts/Sections';
import { ISectionData } from '../../../components/layouts/Sections/Section/Section.types';
import { MainSource } from '../../../constants/Source';
import { useTrackSearchAndFiltersAnalytics } from '../../../utils/hooks/bi/useTrackSearchAndFiltersAnalytics';
import {
  ClickOrigins,
  ErrorTypes,
} from '../../../utils/hooks/bi/useTrackSearchAndFiltersAnalytics/useTrackSearchAndFiltersAnalytics.types';
import { EmptyState } from '../EmptyState';
import { IOuter } from './SearchResults.types';
import { useSections } from './useSections';
import styles from './SearchResults.scss';
import { getItemSource } from '../../../utils/media-manager-sdk/items';
import { useSearchResultsBI } from './useSearchResultsBI';

const getStringifiedSectionsList = (sections: ISectionData[]): string => JSON.stringify(pluck('sectionName', sections));

function isInViewport(element: Element) {
  const rect = element.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
}

export const SearchResults: FC<IOuter> = ({
  searchQuery,
  refreshFolder,
  beginDrag,
  isItemSelected,
  onItemClick,
  onItemRightClick,
  onDropOnFolder,
  onFolderDoubleClick,
  onFileDoubleClick,
  openExpandedView,
  selectedItems,
  buyItem,
  settingsLayout,
  items,
  currentFolder,
  isLoading,
}) => {
  const sections = useSections({ items, searchQuery });

  const { trackSearchAndFiltersShowAllClick } = useTrackSearchAndFiltersAnalytics();

  const handleShowAllClick = useCallback(
    (sectionIndex) =>
      trackSearchAndFiltersShowAllClick({
        target: sectionIndex.toString(),
        sectionsAmount: sections.length,
        sectionsArray: getStringifiedSectionsList(sections),
        clickOrigin: ClickOrigins.Search,
      }),
    [sections, trackSearchAndFiltersShowAllClick],
  );

  const { trackSearchAndFiltersFinished } = useTrackSearchAndFiltersAnalytics();
  const wasLoading = usePrevious(isLoading);
  const { setSearchMetaInfo, resetSearchBI } = useSearchResultsBI();
  useEffect(() => {
    if (wasLoading && !isLoading) {
      const searchMetaInfo = {};
      sections.forEach((section, sectionIndex) => {
        section.data.forEach((item, indexWithinSection) => {
          searchMetaInfo[item.id] = {
            sectionName: section.sectionName,
            sectionIndex: sectionIndex + 1,
            itemIndexWithinCategory: indexWithinSection + 1,
          };
        });
      });
      setSearchMetaInfo(searchMetaInfo);

      const visibleItems = Array.from(document.querySelectorAll('[data-hook-item-id]'))
        .filter(isInViewport)
        .map((x) => x.getAttribute('data-hook-item-id'));

      const biSections = countBy(getItemSource, items);
      trackSearchAndFiltersFinished(
        items.length
          ? {
              success: true,
              sectionsArray: JSON.stringify(biSections),
              sectionsAmount: Object.keys(biSections).length,
              numOfResults: items.length,
              section_name_list: sections.map((x) => x.sectionName).join(','),
              file_list: visibleItems.join(','),
            }
          : {
              success: false,
              numOfResults: 0,
              errorType: ErrorTypes.NoResults,
            },
      );
    }
  }, [isLoading, wasLoading, items, sections, trackSearchAndFiltersFinished, setSearchMetaInfo]);
  useEffect(
    () => () => {
      resetSearchBI();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  if (isLoading) {
    return <FullHeightLoader size="medium" text={<FormattedMessage id="search.searching" />} />;
  }

  return (
    <div className={styles.holder}>
      <div className={styles.scrollContainer}>
        {sections.length ? (
          <SectionsLayout
            selectedItems={selectedItems}
            sections={sections}
            beginDrag={beginDrag}
            rowsInSection={1}
            isItemSelected={isItemSelected}
            onItemClick={onItemClick}
            onItemRightClick={onItemRightClick}
            onShowAllClick={handleShowAllClick}
            onDropOnFolder={onDropOnFolder}
            onFileDoubleClick={onFileDoubleClick}
            onFolderDoubleClick={onFolderDoubleClick}
            openExpandedView={openExpandedView}
            buyItem={buyItem}
            currentFolder={currentFolder}
            source={MainSource.SiteFiles}
            dataHook="search-results"
            searchQuery={searchQuery}
          />
        ) : (
          <EmptyState
            refreshFolder={refreshFolder}
            source={MainSource.All}
            searchQuery={searchQuery}
            settingsLayout={settingsLayout}
            dataHook="search-results"
          />
        )}
      </div>
    </div>
  );
};
